import { createSelector } from 'reselect';
import { AxiosError } from 'axios';

import { ActionType, LoadingStatusType, ErrorTypes } from 'interfaces';
import { FAILURE, SUCCESS } from 'services/api';
import {
  PRODUCTS,
  PRODUCT_RANGES,
  PRODUCT_BRANDS,
  SET_PRODUCT_BRANDS_LOADING_STATUS,
  SET_PRODUCT_RANGES_LOADING_STATUS,
  SET_PRODUCTS_LOADING_STATUS,
} from 'actions/product.actions';
import { LOADING_STATUS } from 'constants/status';

export type Brand = {
  brand: string;
};
export type Range = {
  range: string;
};

interface IProductSubType<SubType> {
  status: LoadingStatusType;
  data: SubType[];
  error: AxiosError | null;
}

export interface IProductState {
  brands: IProductSubType<Brand>;
  ranges: IProductSubType<Range>;
  status: LoadingStatusType;
  error: ErrorTypes | null;
}

export const initialState: IProductState = {
  brands: {
    status: LOADING_STATUS.IDLE,
    data: [],
    error: null,
  },
  ranges: {
    status: LOADING_STATUS.IDLE,
    data: [],
    error: null,
  },
  status: LOADING_STATUS.IDLE,
  error: null,
};

export const productReducerSelector = (state: {
  product: IProductState;
}): IProductState => state.product;

export const rangesSelector = createSelector(
  productReducerSelector,
  (product) => product.ranges,
);
export const brandsSelector = createSelector(
  productReducerSelector,
  (product) => product.brands,
);

export const productReducer = (
  state: IProductState = initialState,
  action: ActionType,
): IProductState => {
  const { payload } = action;

  switch (action.type) {
    case SET_PRODUCT_RANGES_LOADING_STATUS:
      return {
        ...state,
        ranges: {
          ...state.ranges,
          status: payload.status,
        },
      };
    case PRODUCT_RANGES[FAILURE]:
      return {
        ...state,
        ranges: {
          ...state.ranges,
          data: [],
          error: payload.error,
        },
      };
    case PRODUCT_RANGES[SUCCESS]:
      return {
        ...state,
        ranges: {
          ...state.ranges,
          data: payload.data,
          error: null,
        },
      };
    case SET_PRODUCT_BRANDS_LOADING_STATUS:
      return {
        ...state,
        brands: {
          ...state.brands,
          status: payload.status,
        },
      };
    case PRODUCT_BRANDS[FAILURE]:
      return {
        ...state,
        brands: {
          ...state.brands,
          data: [],
          error: payload.error,
        },
      };
    case PRODUCT_BRANDS[SUCCESS]:
      return {
        ...state,
        brands: {
          ...state.brands,
          data: payload.data,
          error: null,
        },
      };
    case SET_PRODUCTS_LOADING_STATUS:
      return {
        ...state,
        status: payload.status,
      };
    case PRODUCTS[FAILURE]:
      return {
        ...state,
        error: payload.error,
      };
    case PRODUCTS[SUCCESS]:
      return {
        ...state,
        error: null,
      };
    default: {
      return state;
    }
  }
};

export default productReducer;
